import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import booksData from './booksData';
import './Books.css';

const Books = ({ category }) => {
  const [filteredBooks, setFilteredBooks] = useState([]);

  useEffect(() => {
    const filteredBooks = booksData
      .map(series => ({
        ...series,
        books: series.books.filter(book =>
          book.showBook && (category === 'All Books' || book.category.toLowerCase() === category.toLowerCase())
        )
      }))
      .filter(series => series.books.length > 0);

    setFilteredBooks(filteredBooks);
  }, [category]);

  const sortedReadingOrderBooks = filteredBooks
    .flatMap((series) => series.books)
    .filter((book) => book.readingOrder)
    .sort((a, b) => parseInt(a.readingOrder) - parseInt(b.readingOrder));

  const showReadingOrder = category !== 'All Books' && sortedReadingOrderBooks.length > 1;
  const showReadingOrderSection = showReadingOrder && filteredBooks.length > 1;

  let pageTitle = 'Books by Jerry Evanoff | Murder Mystery and Time Travel Fiction';
  let pageDescription =
    'Explore books by Jerry Evanoff, including the Sam Norris Murder Mystery Series, modern amateur sleuth whodunits, soft-boiled murder mysteries, and time travel science fiction.';
  let pageKeywords =
    'Jerry Evanoff books, Sam Norris Murder Mystery Series, amateur sleuth murder mystery, modern whodunit, soft-boiled mystery, tech-savvy sleuth, time travel fiction';
  let canonicalUrl = 'https://jerryevanoff.com/books';

  if (category === 'murderMystery') {
    pageTitle = 'Murder Mystery Books by Jerry Evanoff | Sam Norris Mystery Series';
    pageDescription =
      'Explore the Sam Norris Murder Mystery Series by Jerry Evanoff, featuring a tech-savvy programmer and amateur sleuth solving modern whodunit mysteries.';
    pageKeywords =
      'Jerry Evanoff mystery books, Sam Norris Murder Mystery Series, amateur sleuth murder mystery, modern whodunit, soft-boiled mystery, tech-savvy sleuth, programmer detective, Agatha Christie-style mystery';
    canonicalUrl = 'https://jerryevanoff.com/murder-mystery';
  }

  if (category === 'timeTravel') {
    pageTitle = 'Time Travel Books by Jerry Evanoff | Science Fiction Novels';
    pageDescription =
      'Explore time travel science fiction books by Jerry Evanoff, including stories about memory, timelines, second chances, and the consequences of changing the future.';
    pageKeywords =
      'Jerry Evanoff time travel books, time travel fiction, science fiction novels, timeline stories, memory science fiction, indie science fiction author';
    canonicalUrl = 'https://jerryevanoff.com/time-travel';
  }

  const makeAbsoluteUrl = (url) => {
    if (!url) {
      return 'https://jerryevanoff.com';
    }

    if (url.startsWith('http')) {
      return url;
    }

    return `https://jerryevanoff.com${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    url: canonicalUrl,
    description: pageDescription,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredBooks
        .flatMap((series) => series.books)
        .map((book, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Book',
            name: book.title,
            author: {
              '@type': 'Person',
              name: 'Jerry Evanoff'
            },
            url: book.webLink ? makeAbsoluteUrl(book.webLink) : (book.buyLink || canonicalUrl)
          }
        }))
    }
  };

  return (
    <section className="books">
      <Helmet>
        <title>{pageTitle}</title>

        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <meta name="author" content="Jerry Evanoff" />

        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Jerry Evanoff" />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />

        <script type="application/ld+json">
          {JSON.stringify(collectionSchema)}
        </script>
      </Helmet>

      <div className="books-container">
        <div className="my-books">
          {filteredBooks.map((series, seriesIndex) => (
            <div key={seriesIndex} className="books-series">
              <h3 className="series-title">{series.seriesName}</h3>

              <div className="books-row">
                {series.books.map((book, bookIndex) => (
                  <div key={bookIndex} className="book">
                    {book.webLink ? (
                      <Link to={book.webLink}>
                        <img src={book.cover} alt={book.title} className="book-cover" />
                      </Link>
                    ) : (
                      <a href={book.buyLink} target="_blank" rel="noopener noreferrer">
                        <img src={book.cover} alt={book.title} className="book-cover" />
                      </a>
                    )}

                    <p className="book-description">{book.shortDescription}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {showReadingOrderSection && (
          <div className="reading-order">
            <h5 className="reading-order-label">Suggested Reading Order</h5>
            <hr className="reading-order-divider" />

            {sortedReadingOrderBooks.map((book) => (
              <div key={book.title} className="reading-order-item">
                <span className="reading-order-title">
                  <strong>{book.title}</strong>
                </span>

                {book.readingOrderDescription && (
                  <p className="reading-order-description">{book.readingOrderDescription}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Books;